import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { verifyEmail as verifyEmailApi } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);

    try {
      await verifyEmailApi({ email, code });

      toast({
        title: "Success!",
        description: "Your email has been verified successfully.",
      });

      navigate('/verify-success');
    } catch (error: any) {
      console.error('Verification error:', error);
      toast({
        title: "Verification failed",
        description: error.message || "Invalid verification code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="flex justify-center">
          <Mail className="w-20 h-20 text-primary" />
        </div>
        
        <h1 className="text-3xl font-bold text-center text-foreground">
          Verify Your Email
        </h1>
        
        <p className="text-center text-muted-foreground">
          We've sent a verification code to <strong>{email}</strong>. Please enter the code below.
        </p>
        
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Enter 8-character code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
              maxLength={8}
              className="text-center text-2xl tracking-widest"
              required
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={isVerifying || code.length !== 8}
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </Button>
        </form>
        
        <div className="text-center">
          <Button 
            variant="link" 
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
